import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";
import type { ReactNode } from "react";
import {
  Lightbulb,
  GitBranch,
  Package,
  Target,
  Scale,
  Zap,
  ArrowRight,
  Play,
  CheckCircle2,
  Layers,
  ListChecks,
  Activity,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDecisions } from "@/hooks/queries/use-decisions";
import { useSessionEvents } from "@/hooks/useSessionEvents";
import { SessionEventType } from "@spectree/shared";

/* ── Feed item types ── */
type FeedItem =
  | { kind: "decision"; ts: string; icon: ReactNode; color: string; label: string; text: string; detail?: string; category?: string }
  | { kind: "event"; ts: string; icon: ReactNode; color: string; label: string; text: string };

const categoryIcon: Record<string, ReactNode> = {
  approach: <Lightbulb className="h-3.5 w-3.5" />,
  architecture: <GitBranch className="h-3.5 w-3.5" />,
  library: <Package className="h-3.5 w-3.5" />,
  scope: <Target className="h-3.5 w-3.5" />,
  tradeoff: <Scale className="h-3.5 w-3.5" />,
};

const eventIcon: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  [SessionEventType.SESSION_STARTED]: { icon: <Play className="h-3.5 w-3.5" />, color: "text-blue-400/60 border-blue-400/20", label: "session started" },
  [SessionEventType.SESSION_ENDED]: { icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: "text-emerald-400/60 border-emerald-400/20", label: "session ended" },
  [SessionEventType.SESSION_PHASE_STARTED]: { icon: <Layers className="h-3.5 w-3.5" />, color: "text-blue-400/60 border-blue-400/20", label: "phase started" },
  [SessionEventType.SESSION_PHASE_COMPLETED]: { icon: <Layers className="h-3.5 w-3.5" />, color: "text-emerald-400/60 border-emerald-400/20", label: "phase completed" },
  [SessionEventType.SESSION_FEATURE_STARTED]: { icon: <Package className="h-3.5 w-3.5" />, color: "text-blue-400/60 border-blue-400/20", label: "feature started" },
  [SessionEventType.SESSION_FEATURE_COMPLETED]: { icon: <Package className="h-3.5 w-3.5" />, color: "text-emerald-400/60 border-emerald-400/20", label: "feature completed" },
  [SessionEventType.SESSION_TASK_STARTED]: { icon: <ListChecks className="h-3.5 w-3.5" />, color: "text-blue-400/60 border-blue-400/20", label: "task started" },
  [SessionEventType.SESSION_TASK_COMPLETED]: { icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: "text-emerald-400/60 border-emerald-400/20", label: "task completed" },
  [SessionEventType.SESSION_TASK_PROGRESS]: { icon: <Activity className="h-3.5 w-3.5" />, color: "text-white/50 border-white/[0.06]", label: "progress" },
  [SessionEventType.SESSION_ERROR]: { icon: <AlertCircle className="h-3.5 w-3.5" />, color: "text-red-400/60 border-red-400/20", label: "error" },
};

/* ── Component ── */
interface ActivityFeedProps {
  epicId: string;
  className?: string;
}

export function ActivityFeed({ epicId, className }: ActivityFeedProps) {
  const { data: decisionsData } = useDecisions({ epicId });
  const { events } = useSessionEvents({ epicId, enabled: true, refetchInterval: 3000 });

  const items = useMemo(() => {
    const feedItems: FeedItem[] = [];

    // Map decisions to feed items
    for (const d of decisionsData?.data ?? []) {
      feedItems.push({
        kind: "decision",
        ts: d.createdAt,
        icon: categoryIcon[d.category] ?? <Lightbulb className="h-3.5 w-3.5" />,
        color: "",
        label: "decision",
        text: d.decision,
        detail: d.rationale,
        category: d.category,
      });
    }

    // Map session events to feed items
    for (const e of events) {
      const meta = eventIcon[e.eventType] ?? { icon: <Activity className="h-3.5 w-3.5" />, color: "text-white/50 border-white/[0.06]", label: e.eventType };
      const payload = e.payload as Record<string, unknown> | undefined;
      const text = (payload?.title as string | undefined) ?? (payload?.message as string | undefined) ?? meta.label;
      feedItems.push({
        kind: "event",
        ts: e.timestamp,
        icon: meta.icon,
        color: meta.color,
        label: meta.label,
        text,
      });
    }

    // Sort newest first
    feedItems.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
    return feedItems;
  }, [decisionsData, events]);

  return (
    <div className={cn("flex flex-col", className)}>
      <Header count={items.length} />

      {/* Feed */}
      <div className="flex-1 overflow-y-auto min-h-0" style={{ scrollbarWidth: "thin" }}>
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Zap className="h-6 w-6 text-white/50 mb-2" />
            <p className="text-xs text-white/50">No activity yet</p>
            <p className="text-xs text-white/50 mt-0.5">Decisions and events will appear here during execution</p>
          </div>
        ) : (
          <div className="px-3 py-2 space-y-0.5">
            {items.map((item, i) => (
              <FeedRow key={`${item.kind}:${item.ts}`} item={item} isLast={i === items.length - 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function Header({ count }: { count: number }) {
  return (
    <div className="px-4 py-3 border-b border-white/[0.06] shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-white/50" />
          <span className="text-xs font-medium text-white/50 uppercase tracking-wider">
            Activity
          </span>
          {count > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/[0.06] px-1.5 text-xs font-medium text-white/50">
              {count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FeedRow({ item, isLast }: { item: FeedItem; isLast: boolean }) {
  if (item.kind === "decision") return <DecisionRow item={item} isLast={isLast} />;
  return <EventRow item={item} isLast={isLast} />;
}

function DecisionRow({ item, isLast }: { item: FeedItem & { kind: "decision" }; isLast: boolean }) {
  return (
    <div className="group relative flex gap-3 px-2 py-2.5 rounded-lg hover:bg-white/[0.03] transition-colors">
      {!isLast && <div className="absolute left-[19px] top-[28px] bottom-0 w-px bg-white/[0.04]" />}
      <div className={cn(
        "relative z-10 shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-white/[0.04] border border-white/[0.06]",
        "text-yellow-400/60"
      )}>
        {item.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white/70 leading-snug line-clamp-2">
          {item.text}
        </p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-xs text-white/50 font-medium bg-white/[0.04] px-1 rounded">decision</span>
          {item.category && <span className="text-xs text-white/50 capitalize">{item.category}</span>}
          <span className="text-white/30">&middot;</span>
          <span className="text-xs text-white/50">
            {formatDistanceToNow(new Date(item.ts), { addSuffix: true })}
          </span>
        </div>
        {item.detail && (
          <div className="hidden group-hover:flex items-start gap-1 mt-1.5 text-xs text-white/50 leading-relaxed">
            <ArrowRight className="h-2.5 w-2.5 mt-0.5 shrink-0 text-white/50" />
            <span className="line-clamp-3">{item.detail}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function EventRow({ item, isLast }: { item: FeedItem & { kind: "event" }; isLast: boolean }) {
  return (
    <div className="group relative flex gap-3 px-2 py-2.5 rounded-lg hover:bg-white/[0.03] transition-colors">
      {!isLast && <div className="absolute left-[19px] top-[28px] bottom-0 w-px bg-white/[0.04]" />}
      <div className={cn(
        "relative z-10 shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-white/[0.04] border",
        item.color
      )}>
        {item.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white/70 leading-snug line-clamp-2">
          {item.text}
        </p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-xs text-white/50">{item.label}</span>
          <span className="text-white/30">&middot;</span>
          <span className="text-xs text-white/50">
            {formatDistanceToNow(new Date(item.ts), { addSuffix: true })}
          </span>
        </div>
      </div>
    </div>
  );
}
